import type { SimplifiedExperienceIneligibleReason } from "./simplifiedExperienceDecision";

type OfferReviewSimplifiedIneligibleLabelOptions = {
  breezeCreatedDateCutoff?: string | undefined;
};

const formatBreezeCreatedDateCutoffForDisplay = (
  cutoff: string
): string | null => {
  const parsed = Date.parse(cutoff);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return new Date(parsed).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const OFFER_REVIEW_SIMPLIFIED_INELIGIBLE_LABELS: Record<
  SimplifiedExperienceIneligibleReason,
  string
> = {
  FEATURE_FLAG_OFF: "Breeze is not turned on in this environment yet.",
  MARKET_NOT_TARGETED:
    "This customer's state or utility is not included in the Breeze rollout yet.",
  FLAGS_NOT_INITIALISED:
    "We're still checking whether Breeze is available for this offer.",
  FLAGS_LOADING:
    "We're still checking whether Breeze is available for this offer.",
  MISSING_OFFER_CREATED_AT:
    "We couldn't confirm when this proposal was created, so Breeze isn't available.",
  OFFER_CREATED_BEFORE_CUTOFF:
    "This proposal is too old for Breeze. Only proposals created after the rollout date qualify.",
  MISSING_OFFER: "Offer details aren't loaded yet, so Breeze isn't available.",
  SECOND_SYSTEM: "Breeze isn't available for add-on (second system) offers.",
  MISSING_SELF_SUPPLY:
    "This offer doesn't include the self-supply information Breeze needs.",
  OFFER_PRODUCT_INELIGIBLE:
    "This offer type isn't supported in Breeze. Breeze is for full solar and battery systems.",
  CAP_PAGE_LOADING: "Breeze isn't available for this presentation.",
  CHANGE_ORDER_MISSING_ORIGINAL_SELF_SUPPLY:
    "Breeze isn't available for this presentation.",
  NON_SOLAR_OR_STORAGE_ONLY: "Breeze isn't available for this presentation.",
  NOT_SOLAR_AND_BATTERY_PACKAGE:
    "Breeze isn't available for this presentation.",
};

const getOfferReviewSimplifiedIneligibleReasonLabel = (
  reason: SimplifiedExperienceIneligibleReason,
  options?: OfferReviewSimplifiedIneligibleLabelOptions
): string => {
  if (reason === "OFFER_CREATED_BEFORE_CUTOFF") {
    const formatted = options?.breezeCreatedDateCutoff
      ? formatBreezeCreatedDateCutoffForDisplay(options.breezeCreatedDateCutoff)
      : null;

    if (formatted) {
      return `This proposal is too old for Breeze. Only proposals created after ${formatted} qualify.`;
    }
  }

  return OFFER_REVIEW_SIMPLIFIED_INELIGIBLE_LABELS[reason];
};

const CLASSIC_VIEW_TOOLTIP_PREFIX =
  "The customer's offer is unaffected. You are seeing the classic view on purpose because: ";

const getOfferReviewClassicViewTooltipMessage = (
  reason: SimplifiedExperienceIneligibleReason,
  options?: OfferReviewSimplifiedIneligibleLabelOptions
): string =>
  `${CLASSIC_VIEW_TOOLTIP_PREFIX}${getOfferReviewSimplifiedIneligibleReasonLabel(
    reason,
    options
  )}`;

export {
  CLASSIC_VIEW_TOOLTIP_PREFIX,
  formatBreezeCreatedDateCutoffForDisplay,
  getOfferReviewClassicViewTooltipMessage,
  getOfferReviewSimplifiedIneligibleReasonLabel,
};
export type { OfferReviewSimplifiedIneligibleLabelOptions };
